import * as React from 'react';
import styled from 'styled-components';
import {DropdownHandler, Icon} from '@forecast-it/design-system';

// Quiet at rest: the current value plus a caret, with no border or fill of its own,
// so a panel of these reads as values rather than as a stack of controls. The caret
// is the only thing saying "this can change".
const Trigger = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
	border: 1px solid transparent;
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	background: transparent;
	padding: ${p => p.theme.sys.spacing[100]};
	margin: 0;
	cursor: pointer;
	text-align: left;

	&:hover:not(:disabled) {
		background: ${p => p.theme.sys.color.background.disabled['1']};
	}
	&:disabled {
		cursor: default;
	}
`;
const OptionRow = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
// A fixed slot for the tick, so the options line up whether or not one is chosen.
const TickSlot = styled.span`
	display: inline-flex;
	justify-content: center;
	flex: none;
	width: 16px;
`;

interface Props<T extends string> {
	/** Names the control for assistive tech — the trigger's content is a value, not a label. */
	label: string;
	options: readonly T[];
	current: T;
	onSelect: (value: T) => void;
	/** How the current value reads at rest. */
	renderValue: (value: T) => React.ReactNode;
	/** How each option reads in the menu. Defaults to `renderValue`. */
	renderOption?: (value: T) => React.ReactNode;
	disabled?: boolean;
	'data-cy'?: string;
}

/**
 * A value in the Details panel that opens a menu of the values it could be.
 *
 * For a small, fixed set — a status, a priority, a channel — where a picker would be
 * more chrome than the choice deserves. The chosen option carries a tick, so the menu
 * says what the current value is without relying on the trigger still being visible.
 *
 * The menu is Radix-backed: it opens on pointerdown and its items are portalled, so
 * they do not exist in the DOM until it is open.
 */
export function MetaChip<T extends string>({
	label,
	options,
	current,
	onSelect,
	renderValue,
	renderOption,
	disabled = false,
	'data-cy': dataCy,
}: Props<T>) {
	const option = renderOption ?? renderValue;

	if (disabled) {
		// With no permission to change it, the value is just a value — no caret, no
		// trigger, nothing to press.
		return <span data-cy={dataCy}>{renderValue(current)}</span>;
	}

	return (
		<DropdownHandler>
			<DropdownHandler.Trigger>
				<Trigger type="button" aria-label={label} data-cy={dataCy}>
					{renderValue(current)}
					<Icon icon="caretDown" size="s" newColor="disabled" />
				</Trigger>
			</DropdownHandler.Trigger>
			<DropdownHandler.Content offset={4}>
				{options.map(value => (
					<DropdownHandler.Item key={value} onClick={() => onSelect(value)}>
						<OptionRow>
							<TickSlot>{value === current && <Icon icon="tick" size="s" newColor="primary" />}</TickSlot>
							{option(value)}
						</OptionRow>
					</DropdownHandler.Item>
				))}
			</DropdownHandler.Content>
		</DropdownHandler>
	);
}
